import { useCallback } from 'react'
import { put } from '../../../utilities/http'
import { storagePage } from '../storage'

/**
 * Hook para auto-guardar cambios al avanzar entre pasos
 * Evita pérdida de datos y mantiene sincronizada la versión
 *
 * Funciona en ambos modos:
 * - Creación: Guarda después del paso 1 (cuando ya existe la declaración)
 * - Edición: Guarda en cada paso intermedio
 */
export default function useAutoSave() {
    const autoSave = useCallback(async (currentStep: string) => {
        // En modo creación, solo auto-guardar después del paso 1
        if (storagePage.mode === 'creation') {
            // En paso 1, no hay nada que guardar aún (se guarda con submitStep1)
            if (currentStep === 'step1') {
                return {
                    success: true,
                    skipped: true,
                    reason: 'step1_handles_save'
                }
            }

            // Después del paso 1, si no hay declaración, no podemos guardar
            if (!storagePage.logistical_declaration) {
                return {
                    success: true,
                    skipped: true,
                    reason: 'no_declaration_yet'
                }
            }
        }

        // En modo edición, verificar que haya versión
        if (storagePage.mode === 'edition') {
            if (!storagePage.version || !storagePage.logistical_declaration) {
                return { success: true, skipped: true, reason: 'no_version' }
            }
        }

        try {
            console.log(
                `[AutoSave] Guardando cambios automáticamente (${storagePage.mode}, ${currentStep})...`
            )

            const version = storagePage.version || '1' // En creación, usar versión "1"

            const response = await put(
                `/v5.6/apis/logistical_declarations/${storagePage.logistical_declaration.id}/save_logistical_costs`,
                {
                    version: version
                }
            )

            if (response.error) {
                console.error('[AutoSave] Error al guardar:', response.error)
                return { success: false, error: response.error }
            }

            console.log('[AutoSave] Cambios guardados exitosamente')

            // Actualizar current_version si es necesario
            if (storagePage.logistical_declaration) {
                storagePage.logistical_declaration.current_version = version
            }

            return { success: true, skipped: false }
        } catch (error) {
            console.error('[AutoSave] Excepción al guardar:', error)
            return { success: false, error }
        }
    }, [])

    return autoSave
}
